import type { Currency, Product } from '@vy/types';
import { COMMITMENT_TYPES } from '@vy/types';
import { isUsageProduct } from '@vy/services';
import { isNotNullOrUndefined, raiseUnreachable } from '@vy/utils';
import React, { useMemo, useState } from 'react';
import { VyDialog } from '../../../../../../../../components/new-design/feedback/VyDialog';
import { useCreditPoolsList } from '../../../../../../../../hooks/requests/use-credit-pools-list';
import { useExchangeRates } from '../../../../../../../../hooks/requests/exchange-rates/use-exchange-rates';
import { formatCommitmentAmount } from '../../../../../../../../services/commitment/formatCommitmentAmount';
import type { ProductUsageBreakdownDateResolution } from '../../ViewProducts/ProductUsageBreakdownChart/product-usage-breakdown-chart-types/product-usage-breakdown-date-resolution';
import { PRODUCT_USAGE_BREAKDOWN_DATE_RESOLUTIONS } from '../../ViewProducts/ProductUsageBreakdownChart/product-usage-breakdown-chart-types/product-usage-breakdown-date-resolution';
import { CreditsTracker } from './components/CreditsTracker/CreditsTracker.tsx';
import type { CommitmentTotals } from './ContractUsageDialogContent';
import { ContractUsageDialogContent } from './ContractUsageDialogContent';
import { useContractUsageBreakdown } from './use-contract-usage-breakdown';
import { CREDIT_POOL_ENTRY_TYPES } from '@vy/api-definition';

type ContractUsageDialogProps = {
  open: boolean;
  onClose: () => void;
  contractId: string;
  contractName: string;
  customerId: string;
  products: Product[];
  currency: Currency;
};

export const ContractUsageDialog: React.FC<ContractUsageDialogProps> = ({
  open,
  onClose,
  contractId,
  contractName,
  customerId,
  products,
  currency,
}) => {
  const [dateResolution, setDateResolution] = useState<ProductUsageBreakdownDateResolution>(
    PRODUCT_USAGE_BREAKDOWN_DATE_RESOLUTIONS.MONTH,
  );
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);

  const { isLoading, usageByDateBreakdowns, usageProducts } = useContractUsageBreakdown(contractId);
  const { data: exchangeRates } = useExchangeRates();
  const { data: creditPoolsData } = useCreditPoolsList(customerId, { suspense: false });
  // TODO: multi-pool aggregation is future work — see specs/credit-products-future-work.md.
  const [creditPool] = creditPoolsData?.creditPools ?? [];

  const creditPoolTotals = useMemo(() => {
    if (!creditPool) {
      return null;
    }

    const totalCredits = creditPool.entries.reduce(
      (sum, { type, amount }) => (type === CREDIT_POOL_ENTRY_TYPES.GRANT ? sum + amount : sum),
      0,
    );

    return { totalCredits, usedCredits: totalCredits - creditPool.balance };
  }, [creditPool]);

  const commitmentTotals = useMemo<CommitmentTotals>(() => {
    const usageProductsWithCommitments = products.filter(
      (product): product is Product & { commitment: NonNullable<Product['commitment']> } =>
        isUsageProduct(product) && isNotNullOrUndefined(product.commitment),
    );

    const totals = usageProductsWithCommitments.reduce<{ units?: number; price?: number }>((acc, product) => {
      switch (product.commitment.type) {
        case COMMITMENT_TYPES.Units:
          return { ...acc, units: (acc.units ?? 0) + product.commitment.amount };
        case COMMITMENT_TYPES.Price:
          return { ...acc, price: (acc.price ?? 0) + product.commitment.amount };
        default:
          return raiseUnreachable(product.commitment.type);
      }
    }, {});

    return {
      units: isNotNullOrUndefined(totals.units)
        ? {
            amount: totals.units,
            formatted: formatCommitmentAmount(COMMITMENT_TYPES.Units, totals.units, currency, exchangeRates),
          }
        : undefined,
      price: isNotNullOrUndefined(totals.price)
        ? {
            amount: totals.price,
            formatted: formatCommitmentAmount(COMMITMENT_TYPES.Price, totals.price, currency, exchangeRates),
          }
        : undefined,
    };
  }, [products, currency, exchangeRates]);

  return (
    <VyDialog
      open={open}
      onClose={onClose}
      label={contractName}
      disableDividers
      maxWidth="md"
      fullWidth
      sx={{ zIndex: 10000 }}
      dialogTitleProps={{ sx: { paddingBottom: 0 } }}
      dialogContentProps={{
        style: {
          paddingTop: 0,
        },
      }}
    >
      <ContractUsageDialogContent
        isLoading={isLoading}
        usageByDateBreakdowns={usageByDateBreakdowns}
        usageProducts={usageProducts}
        currency={currency}
        dateResolution={dateResolution}
        selectedProductIds={selectedProductIds}
        commitmentTotals={commitmentTotals}
        creditsSlot={
          creditPool && creditPoolTotals && creditPool.closestEntryExpiryDate != null ? (
            <CreditsTracker
              schedule={creditPool.initialGrantSchedule}
              totalCredits={creditPoolTotals.totalCredits}
              usedCredits={creditPoolTotals.usedCredits}
              resetDate={creditPool.closestEntryExpiryDate}
            />
          ) : undefined
        }
        onDateResolutionChange={setDateResolution}
        onSelectedProductsChange={setSelectedProductIds}
      />
    </VyDialog>
  );
};
